// apps/client/src/ui-kit/Icon.tsx
//
// Centralized icon component — renders custom SVG icons
// All icons are user-provided SVGs for a polished, hand-picked look.
//

import React from 'react';

// ─── SVG Imports ───────────────────────────────────────────────
// Navigation & Actions
import icClose from '../assets/icons/close.svg';
import icArrowBack from '../assets/icons/previous.svg';
import icArrowForward from '../assets/icons/arrow-forward.svg';
import icCheck from '../assets/icons/check-mark.svg';
import icShuffle from '../assets/icons/shuffle.svg';
import icAdd from '../assets/icons/add.svg';
import icRefresh from '../assets/icons/refresh-page-option.svg';
import icSettings from '../assets/icons/setting.svg';

// Social & Users
import icGroupAdd from '../assets/icons/add-user.svg';
import icPerson from '../assets/icons/user.svg';
import icCopy from '../assets/icons/copy.svg';

// Currency & Economy
import icToll from '../assets/icons/perfect-coin.svg';

// Stats & Progress
import icLeaderboard from '../assets/icons/podium.svg';
import icTaskAlt from '../assets/icons/task-done.svg';
import icCheckCircle from '../assets/icons/check-circle.svg';

// Finance
import icWallet from '../assets/icons/wallet.svg';

// Schedule & Events
import icTrophy from '../assets/icons/thropy.svg';

// Legal
import icGavel from '../assets/icons/legal.svg';

// Home & Nav
import icHome from '../assets/icons/home.svg';
import icStore from '../assets/icons/store.svg';

// Effects & Power-ups
import icBolt from '../assets/icons/power-up.svg';
import icGift from '../assets/icons/gift.svg';
import icShoppingBag from '../assets/icons/shopping-bag.svg';
import icShoppingCart from '../assets/icons/shopping-cart-this-one.svg';
import icLocalOffer from '../assets/icons/local_offer.svg';

// Game
import icGamepad from '../assets/icons/gamepad.svg';
import icLink from '../assets/icons/link.svg';
import icShare from '../assets/icons/share.svg';
import icTicket from '../assets/icons/ticket-card.svg';
import icLive from '../assets/icons/live-broadcast.svg';
import icGrid from '../assets/icons/grid.svg';

// Store items
import icDoubleTap from '../assets/icons/double-tap.svg';
import icX2 from '../assets/icons/x2.svg';

// Play
import icPlay from '../assets/icons/play.svg';

// Map icon names → SVG file paths
const iconMap: Record<string, string> = {
    // Navigation & Actions
    close: icClose,
    arrow_back: icArrowBack,
    arrow_forward_ios: icArrowForward,
    arrow_forward: icArrowForward,
    forward: icArrowForward,
    chevron_right: icArrowForward,
    check: icCheck,
    shuffle: icShuffle,
    add: icAdd,
    refresh: icRefresh,
    settings: icSettings,

    // Social & Users
    group_add: icGroupAdd,
    person_add: icGroupAdd,
    person: icPerson,
    content_copy: icCopy,

    // Currency & Economy
    toll: icToll,

    // Stats & Progress
    leaderboard: icLeaderboard,
    task_alt: icTaskAlt,
    check_circle: icCheckCircle,

    // Settings (reuse same icon)
    volume_up: icSettings,
    vibration: icSettings,

    // Finance
    account_balance_wallet: icWallet,
    receipt_long: icWallet,

    // Schedule & Events
    emoji_events: icTrophy,
    schedule: icTrophy,
    calendar_today: icTrophy,

    // Legal
    gavel: icGavel,

    // Home & Nav
    home: icHome,
    military_tech: icTrophy,
    storefront: icStore,

    // Effects & Power-ups
    auto_fix_high: icBolt,
    auto_awesome: icBolt,
    bolt: icBolt,
    star: icBolt,
    stars: icBolt,
    workspace_premium: icTrophy,
    redeem: icGift,
    shopping_bag: icShoppingBag,
    shopping_cart: icShoppingCart,
    local_offer: icLocalOffer,
    looks_two: icX2,

    // Game
    sports_esports: icGamepad,
    gamepad: icGamepad,
    visibility: icLive,
    link: icLink,
    share: icShare,
    target: icGamepad,
    confirmation_number: icTicket,
    live_tv: icLive,
    grid_view: icGrid,

    // Store items
    touch_app: icDoubleTap,
    add_card: icShoppingCart,

    // Play
    play_circle: icPlay,
    play_arrow: icPlay,

    // Admin icons (reuse)
    login: icArrowForward,
    done_all: icCheckCircle,
    celebration: icGift,
    edit: icSettings,
    delete: icClose,
    block: icClose,
    lock_open: icSettings,
    toggle_on: icCheckCircle,
    toggle_off: icClose,
    search: icSettings,
};

interface IconProps {
    name: string;
    className?: string;
    size?: number;
}

/**
 * Custom SVG icon component
 * Renders user-provided SVG icons as <img> elements
 *
 * Usage:
 *   <Icon name="close" className="text-lg" />
 *   <Icon name="home" size={24} />
 */
const Icon: React.FC<IconProps> = ({ name, className = '', size }) => {
    const svgSrc = iconMap[name];

    if (!svgSrc) {
        // Fallback: render nothing gracefully
        console.warn(`[Icon] No SVG mapping for "${name}"`);
        return <span className={className}>•</span>;
    }

    // Parse size from className if not explicitly provided
    let iconSize = size;
    if (!iconSize) {
        if (className.includes('text-5xl')) iconSize = 40;
        else if (className.includes('text-4xl')) iconSize = 36;
        else if (className.includes('text-3xl')) iconSize = 30;
        else if (className.includes('text-2xl')) iconSize = 24;
        else if (className.includes('text-xl')) iconSize = 20;
        else if (className.includes('text-lg')) iconSize = 18;
        else if (className.includes('text-sm')) iconSize = 14;
        else if (className.includes('text-xs')) iconSize = 12;
        else if (className.includes('text-[14px]')) iconSize = 14;
        else iconSize = 20; // default
    }

    // Detect color intent from className before stripping
    const isGold = className.includes('text-primary') || className.includes('text-[#FBBF24]') || className.includes('text-[#FFC107]');
    const isWhite = className.includes('text-white');
    const isHalfWhite = className.includes('text-white/50') || className.includes('text-white/40') || className.includes('text-white/30');

    // Strip text-size and text-color classes from className (we handle via filter)
    const cleanClass = className
        .replace(/text-(5xl|4xl|3xl|2xl|xl|lg|sm|xs|\[14px\])/g, '')
        .replace(/font-black/g, '')
        .replace(/text-\[[^\]]+\]/g, '')
        .replace(/text-\w+[\-\/]?\w*/g, '')
        .trim();

    // Build filter styles: make SVGs white or gold instead of black
    let filterStyle: React.CSSProperties = { display: 'inline-block' };
    if (isGold) {
        filterStyle = { ...filterStyle, filter: 'brightness(0) invert(1) sepia(1) saturate(5) hue-rotate(15deg)' };
    } else if (isHalfWhite) {
        filterStyle = { ...filterStyle, filter: 'brightness(0) invert(1)', opacity: 0.5 };
    } else if (isWhite) {
        filterStyle = { ...filterStyle, filter: 'brightness(0) invert(1)' };
    } else {
        // Default: make white so it's visible on dark backgrounds
        filterStyle = { ...filterStyle, filter: 'brightness(0) invert(1)' };
    }

    return (
        <img
            src={svgSrc}
            alt=""
            width={iconSize}
            height={iconSize}
            className={cleanClass}
            style={filterStyle}
        />
    );
};

export default Icon;
